/**
 * InfraPlanView — V105-6-7, the target plan after the strategy comparison in a FedRAMP / GovRAMP
 * program's Scope tab (`?view=plan`), ADR-0072 §7. Round-1 critic findings applied.
 *
 *   - ONE primary action in the head row, in order: Compare strategies (no decision) → Open the
 *     target plan (a decision signed before plans existed; owner / admin) → Confirm the strategy
 *     (the verdicts changed after it was chosen) → Generate the target plan (with the cutover date
 *     only while no move is open) → Sign the target plan (owner / admin) → Back to Scope. Readers
 *     and leads get an Ask-an-admin primary instead of a dead end.
 *   - One status line: state chip (Draft / Checks passed / Locked) · n of 5 parts · N open items.
 *   - Five parts, each a one-line status with at most two open items (+N more); each open item
 *     names its action, or carries the "Waiting on GAC Pro review" tag (neutral border) when
 *     reviewed content abstains. Display names only; ids, refs and hashes stay behind "Show all".
 *   - Build: the rendered reviewed templates (preview + Download per file) and the code changes.
 *   - V105-7-2: once signed, the primary runs the feasibility checks ("Run feasibility checks"), then
 *     locks the plan (owner / admin; others ask) once every check passed; a locked plan reads
 *     "Locked <date>" with "Open the move". A chosen option with no reviewed check reads "Feasibility
 *     check not defined yet · Waiting on GAC Pro review" (owner decision 2026-10-09: it blocks the lock).
 *   - V105-7-2 critic round 1: the ladder never loops plan ↔ move. After signing: Lock (inline confirm)
 *     → Run feasibility checks (opens the first check's inputs; steps aside while a form is open) →
 *     Get the checks run (the first issued check's hand-off panel) → "Waiting for N results" (no
 *     button) → Choose another option (only a not-defined check left; others see "Waiting on GAC Pro
 *     review" and Back to Scope) → Open the move only once locked. The bold status line is the checks
 *     state; the checks poll every 30 s while a result is due and announce a change once. Regenerating
 *     a locked plan asks first and is owner / admin only (server 403 `plan_locked` for a lead).
 *
 * Reads: any member. Generating: owner / admin or this program's lead (server `can_write`).
 * Opening a target plan, confirming the strategy and signing: owner / admin only.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, Spinner } from '@aigovops/design-system';
import type {
  BuildPlanContent, ControlConfigurationContent, CutoverPlanContent, DeploymentPlanContent, InfraPlan, InfraPlanDocument, PlanGap, PlanGapAction, TargetDesignContent,
} from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { generatePlan, getPlan, getPlanPackage, getPlanVerification, getStrategies, listPlanPackages, listPlans, lockPlanVersion, openPlan, signPlan } from '../../lib/infra-plan-api';
import { isBlocked, PlanFeasibility, RUNNABLE, spikeTitle, type PlanFeasibilityHandle } from './PlanFeasibility';
import { runnerGap } from '../../lib/runner-release';
import { programLabel } from '../../lib/boundary-model';
import { fmtDate } from '../../lib/format';
import { AskAdmin } from '../AskAdmin';
import type { ToastApi } from './program-toast';
import './infra-inventory.css';
import './component-boundary.css';
import './strategy-compare.css';
import './plan-view.css';

type Kind = InfraPlanDocument['kind'];
type Op = 'generate' | 'sign' | 'open' | 'download' | 'lock';
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const PART_LABEL: Record<Kind, string> = {
  target_design: 'What moves', build_plan: 'Build', deployment_plan: 'Deployment', control_configuration: 'Secure configuration', cutover_plan: 'Cutover',
};
const CHANGE_LABEL: Record<TargetDesignContent['services'][number]['change'], string> = {
  replaced: 'Moves', unchanged: 'Stays', partitioned: 'Stays, federal data kept off it', not_in_boundary: 'Not in the boundary yet',
};
const STATE_LABEL: Record<InfraPlan['state'], string> = { draft: 'Draft', spikes_passed: 'Checks passed', locked: 'Locked' };
const ENV_LABEL = { sandbox: 'Sandbox', target: 'Target' } as const;
const WINDOW_LABEL: Record<string, string> = { blocked_rules_not_approved: 'date waits on approved rules', confirm_with_assessor: 'confirm with your assessor', after_cutover: 'after cutover' };
const OP_TEXT: Record<Op, string> = {
  generate: 'The target plan could not be generated.', sign: 'The target plan could not be signed.', open: 'The target plan could not be opened.', download: 'The signed target plan could not be downloaded.',
  lock: 'The target plan could not be locked.',
};
const SHOWN = 2;
/** H9: while a result is due the checks refresh this often (and on window focus). */
export const CHECKS_POLL_MS = 30_000;
const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const errBody = (e: unknown) => (e instanceof ApiError && e.body && typeof e.body === 'object' ? e.body as { code?: string; error?: string; field?: string; plan_id?: string } : {});
// Round-2 Medium: the server's `detail` is the person-readable sentence (never just "Forbidden").
const errMsg = (e: unknown, fallback: string) => { const b = errBody(e) as { error?: string; detail?: string }; return b.detail ?? b.error ?? (e instanceof Error && e.message ? e.message : fallback); };

function docOf<T>(docs: InfraPlanDocument[] | undefined, kind: Kind): T | null {
  const d = docs?.find((x) => x.kind === kind);
  return d ? d.content as unknown as T : null;
}

function saveFile(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function InfraPlanView({ slug, prefix, program, onBack, onOpenInventory, onBackToScope, toastApi }: {
  slug: string; prefix: string; program: string;
  /** Back to the strategy comparison. */
  onBack: () => void;
  onOpenInventory?: () => void;
  onBackToScope?: () => void;
  toastApi?: ToastApi;
}) {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['infra-plan', slug, 'plans', program], queryFn: () => listPlans(slug, program) });
  const current: InfraPlan | null = plans.data?.plans.find((p) => !p.superseded_by_plan_id) ?? null;
  const previous: InfraPlan | null = current?.supersedes_plan_id ? plans.data?.plans.find((p) => p.plan_id === current.supersedes_plan_id) ?? null : null;
  const detail = useQuery({ queryKey: ['infra-plan', slug, 'plan', current?.plan_id], queryFn: () => getPlan(slug, current!.plan_id), enabled: !!current });
  const packages = useQuery({ queryKey: ['infra-plan', slug, 'plan-packages', current?.plan_id], queryFn: () => listPlanPackages(slug, current!.plan_id), enabled: !!current });
  const strategies = useQuery({ queryKey: ['infra-plan', slug, 'strategies', program], queryFn: () => getStrategies(slug, program), enabled: plans.isSuccess && !current });
  // V105-7-2: the feasibility checks and the lock, once every part exists.
  const complete = !!current && !!detail.data && detail.data.plan.missing.length === 0;
  const checks = useQuery({
    queryKey: ['infra-plan', slug, 'verification', current?.plan_id, detail.data?.plan.plan_version_sha256], queryFn: () => getPlanVerification(slug, current!.plan_id), enabled: complete,
    refetchInterval: (q) => (q.state.data?.verification.spikes.some((x) => x.status === 'issued' || x.status === 'awaiting_verified_result') ? CHECKS_POLL_MS : false),
    refetchOnWindowFocus: true,
  });
  const feas = useRef<PlanFeasibilityHandle>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmLock, setConfirmLock] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const confirmLockBtn = useRef<HTMLButtonElement>(null);
  const confirmRegenBtn = useRef<HTMLButtonElement>(null);
  // B3: checks someone handed off in this session (by bundle id) turn "Get the checks run" into a wait.
  const hoKey = `gp-handoff:${current?.plan_id ?? ''}`;
  const [handedOff, setHandedOff] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    try { const raw = window.sessionStorage.getItem(hoKey); setHandedOff(new Set(raw ? JSON.parse(raw) as string[] : [])); } catch { setHandedOff(new Set()); }
  }, [hoKey]);
  const markHandedOff = useCallback((bundleId: string) => setHandedOff((prev) => {
    if (prev.has(bundleId)) return prev;
    const next = new Set(prev); next.add(bundleId);
    try { window.sessionStorage.setItem(hoKey, JSON.stringify([...next])); } catch { /* per-viewer convenience only */ }
    return next;
  }), [hoKey]);

  const [busy, setBusyState] = useState<Op | null>(null);
  // R2: Try again reads the live busy state (a stale closure could start a second request).
  const busyRef = useRef<Op | null>(null);
  const setBusy = (op: Op | null) => { busyRef.current = op; setBusyState(op); };
  const lastOpen = useRef<string | null>(null);
  const [error, setError] = useState<{ text: string; action?: ReactNode } | null>(null);
  const [cutover, setCutover] = useState('');
  const [cutoverError, setCutoverError] = useState('');
  const [forceDate, setForceDate] = useState(false);
  const [live, setLive] = useState('');
  const [more, setMore] = useState<Partial<Record<Kind, boolean>>>({});
  const heading = useRef<HTMLHeadingElement>(null);
  const dateField = useRef<HTMLInputElement>(null);
  const focusAfterLoad = useRef(true);

  const label = programLabel(program);
  const loading = plans.isLoading || (!!current && detail.isLoading);
  // H4: the heading stays mounted; focus lands once the data has rendered (and again after Open / Open the current plan).
  useEffect(() => {
    if (!loading && focusAfterLoad.current) { focusAfterLoad.current = false; heading.current?.focus(); }
  }, [loading, current?.plan_id]);

  const plan = detail.data?.plan ?? current;
  const docs = detail.data?.documents;
  const canWrite = !!detail.data?.can_write;
  const canSign = !!detail.data?.can_sign;
  const canDecide = !!(detail.data?.can_decide ?? plans.data?.can_decide);
  const stale = !!detail.data?.decision_stale;
  const openMove = detail.data?.open_transition ?? null;
  const latestPkg = packages.data?.packages[0] ?? null;
  const signedCurrent = !!latestPkg && !!plan?.plan_version_sha256 && latestPkg.plan_version_sha256 === plan.plan_version_sha256;
  const needsDate = !!plan && (forceDate || (plan.missing.includes('target_design') && !openMove));

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'plans', program] });
    await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'plan'] });
    await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'plan-packages'] });
    await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'verification'] });
    await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'results'] });
  };
  // One announcement: the Scope tab's toast host when there is one, else the local live region.
  const show = (message: string, details?: string) => { if (toastApi) toastApi.show({ message, details, returnFocus: heading.current }); else setLive(details ? `${message}. ${details}` : message); };

  /** H5: the error names its own operation, and "Try again" repeats THAT operation (a failed regenerate never signs). */
  const failure = (e: unknown, op: Op): boolean => {
    const b = errBody(e);
    const fallback = OP_TEXT[op];
    if (b.field === 'planned_cutover_start') { setForceDate(true); setCutoverError('Pick the planned cutover date.'); window.setTimeout(() => dateField.current?.focus(), 0); return true; }
    if (b.code === 'decision_stale') setError({ text: errMsg(e, fallback), action: canDecide ? <Button compact onClick={onBack}>Confirm the strategy</Button> : <AskAdmin slug={slug} what="confirm the strategy" subject={`${label}: confirm the strategy`} compact /> });
    else if (b.code === 'plan_superseded' || b.code === 'plan_exists') setError({ text: errMsg(e, fallback), action: <Button compact onClick={() => { setError(null); void refresh().then(() => heading.current?.focus()); }}>Open the current target plan</Button> });
    else if (b.code === 'decision_superseded') setError({ text: errMsg(e, fallback), action: <Button compact onClick={onBack}>Compare strategies</Button> });
    else if (b.code === 'transition_target_differs' || b.code === 'transition_not_draft') setError({ text: errMsg(e, fallback), action: <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${program}/scope/move`}>Open the move</Link> });
    else if (e instanceof ApiError && e.status === 503) setError({ text: `${fallback} ${op === 'sign' ? 'Signing is' : 'The service is'} not available right now.`, action: <Button compact onClick={() => void retry(op)}>Try again</Button> });
    else setError({ text: errMsg(e, fallback), action: <Button compact onClick={() => void retry(op)}>Try again</Button> });
    return false;
  };

  const generate = async (ev?: FormEvent) => {
    ev?.preventDefault();
    if (!plan || busyRef.current) return;
    const dated = /^\d{4}-\d{2}-\d{2}$/.test(cutover);
    // R2-B1: a strategy that moves nothing needs no date; the field is required only once the server asked for it.
    if (forceDate && !dated) { setCutoverError('Pick the planned cutover date.'); dateField.current?.focus(); return; }
    setBusy('generate'); setError(null); setCutoverError(''); setConfirmRegen(false);
    let fieldFocused = false;
    try {
      const out = await generatePlan(slug, plan.plan_id, needsDate && dated ? { planned_cutover_start: cutover } : {});
      setForceDate(false);
      show(out.created.length ? `Target plan generated · ${plural(out.created.length, 'part')} updated` : 'Target plan is up to date', out.transition_created ? 'The move to the target was opened with your cutover date.' : undefined);
    } catch (e) { fieldFocused = failure(e, 'generate'); } finally {
      // Partial generation keeps the saved parts: always reload.
      await refresh();
      setBusy(null);
      if (!fieldFocused) heading.current?.focus();
    }
  };
  const sign = async () => {
    if (!plan || busyRef.current) return;
    setBusy('sign'); setError(null);
    try {
      const out = await signPlan(slug, plan.plan_id);
      show(`Target plan signed · version ${out.package.version}`, 'Anyone with the verify link can check the signature.');
    } catch (e) { failure(e, 'sign'); } finally { await refresh(); setBusy(null); heading.current?.focus(); }
  };
  const open = async (decisionId: string) => {
    if (busyRef.current) return;
    lastOpen.current = decisionId;
    setBusy('open'); setError(null);
    let ok = false;
    try { await openPlan(slug, decisionId); ok = true; focusAfterLoad.current = true; show('Target plan opened'); } catch (e) { failure(e, 'open'); } finally {
      await refresh(); setBusy(null);
      if (!ok) heading.current?.focus();
    }
  };
  const download = async () => {
    if (!plan || !latestPkg || busyRef.current) return;
    setBusy('download'); setError(null);
    try {
      const r = await getPlanPackage(slug, plan.plan_id, latestPkg.package_id);
      saveFile(`target-plan-${program}-v${latestPkg.version}.json`, JSON.stringify(r, null, 2), 'application/json');
      show('Signed target plan downloaded', r.verification.valid ? 'The signature checks out.' : 'The signature could not be checked right now.');
    } catch (e) { failure(e, 'download'); } finally { setBusy(null); }
  };
  /** Lock the plan version and check results the person sees (owner / admin; 409 when either changed). */
  const lock = async () => {
    const v = checks.data?.verification;
    if (!plan || !v?.plan_version_sha256 || busyRef.current) return;
    setBusy('lock'); setError(null); setConfirmLock(false);
    try {
      await lockPlanVersion(slug, plan.plan_id, { plan_version_sha256: v.plan_version_sha256, spike_set_sha256: v.spike_set_sha256 });
      show('Target plan locked', plan.transition_origin === 'created' ? 'The move can be scheduled now.' : undefined);
    } catch (e) {
      const code = errBody(e).code;
      if (code === 'plan_version_changed' || code === 'spike_set_changed') setError({ text: errMsg(e, OP_TEXT.lock), action: <Button compact onClick={() => { setError(null); void refresh().then(() => heading.current?.focus()); }}>Review it again</Button> });
      else failure(e, 'lock');
    } finally { await refresh(); setBusy(null); heading.current?.focus(); }
  };
  const retry = (op: Op) => (op === 'generate' ? generate() : op === 'sign' ? sign() : op === 'download' ? download() : op === 'lock' ? lock() : lastOpen.current ? open(lastOpen.current) : refresh());
  const copyVerify = async () => {
    if (!latestPkg) return;
    const url = `${location.origin}/verify/infra-plan/${encodeURIComponent(latestPkg.package_id)}`;
    try { await navigator.clipboard.writeText(url); show('Verify link copied', `Version ${latestPkg.version}`); } catch { setError({ text: `Copy this link: ${url}` }); }
  };

  // The move a plan opened waits for its checks (7-2); a joined manual move is the person's own.
  const verification = checks.data?.verification ?? null;
  const spikeList = verification?.spikes ?? [];
  // Round-2 Medium: an unconfirmed result needs its fix (hand-off / trust), not new inputs.
  const runnable = spikeList.filter((s) => !!s.spike_key && RUNNABLE.has(s.status) && s.status !== 'awaiting_verified_result').length;
  const awaitingFix = spikeList.filter((s) => s.status === 'awaiting_verified_result' && s.bundle?.status === 'active').length;
  const passedChecks = spikeList.filter((s) => s.status === 'passed').length;
  const totalChecks = spikeList.length;
  const blockedChecks = spikeList.filter(isBlocked).length;
  // ADR-0074 note 2026-10-09: a chosen move with no resolved target blocks the lock (not a check to run).
  const unresolvedMoves = spikeList.filter((s) => s.status === 'target_unresolved').length;
  const issuedBundles = spikeList.filter((s) => s.status === 'issued' && s.bundle?.status === 'active').map((s) => s.bundle!.bundle_id);
  const notHandedOff = issuedBundles.filter((b) => !handedOff.has(b)).length;
  const resultDue = spikeList.some((s) => s.status === 'issued');
  const lockedMove = !!plan && verification?.state === 'locked' && plan.transition_origin === 'created' && !!openMove?.mine;
  const isLocked = verification?.state === 'locked';
  // Round 3 (F-H2): while GAC Pro hasn't published the runner / signer no check is issued or run.
  const gap = runnerGap();
  const gapChip = gap === 'signer' ? 'Waiting on GAC Pro · bundle signing' : 'Waiting on GAC Pro · runner release';
  const joinedMove = !!plan && plan.transition_origin === 'joined' && !!openMove?.mine;
  const decision = strategies.data?.comparison.latest_decision ?? null;
  const ask = (what: string, lead?: string) => <AskAdmin slug={slug} what={what} subject={`${label}: ${what}`} lead={lead} primary />;

  // ---- the one primary action ----
  let primary: ReactNode = null;
  if (!loading && !plan) {
    if (!decision) primary = <Button compact variant="primary" onClick={onBack}>Compare strategies</Button>;
    else primary = plans.data?.can_decide ? <Button compact variant="primary" aria-busy={busy === 'open' || undefined} onClick={() => void open(decision.decision_id)}>{busy === 'open' ? 'Opening…' : 'Open the target plan'}</Button> : ask('open the target plan');
  } else if (plan && stale) {
    primary = canDecide ? <Button compact variant="primary" onClick={onBack}>Confirm the strategy</Button> : ask('confirm the strategy');
  } else if (plan && plan.missing.length) {
    primary = canWrite ? <Button compact variant="primary" type="submit" form="pv-generate" aria-busy={busy === 'generate' || undefined}>{busy === 'generate' ? 'Generating…' : 'Generate the target plan'}</Button>
      : ask('generate the target plan', 'An owner, admin or the program lead');
  } else if (plan && !signedCurrent) {
    primary = canSign ? <Button compact variant="primary" aria-busy={busy === 'sign' || undefined} onClick={() => void sign()}>{busy === 'sign' ? 'Signing…' : latestPkg ? 'Sign this version' : 'Sign the target plan'}</Button>
      : ask('sign the target plan');
  } else if (plan && (confirmLock || confirmRegen)) {
    // Round-2 Medium: while an inline confirm is open it is the only primary.
    primary = null;
  } else if (plan && verification && verification.state === 'spikes_passed') {
    primary = checks.data?.can_lock ? <Button compact variant="primary" aria-expanded={confirmLock} aria-busy={busy === 'lock' || undefined}
      onClick={() => { setConfirmLock(true); window.setTimeout(() => confirmLockBtn.current?.focus(), 0); }}>{busy === 'lock' ? 'Locking…' : 'Lock the target plan'}</Button>
      : ask('lock the target plan');
  } else if (plan && verification && verification.state === 'draft' && gap && (runnable > 0 || awaitingFix > 0 || issuedBundles.length > 0)) {
    primary = <span className="gp-cb-chip gp-pv-chip" data-tone="info">{gapChip}</span>;
  } else if (plan && verification && verification.state === 'draft' && runnable > 0) {
    // H10: one primary: it steps aside while a check's inputs are open.
    primary = formOpen ? null : checks.data?.can_issue ? <Button compact variant="primary" onClick={() => feas.current?.openFirstRunnable()}>Run feasibility checks</Button>
      : ask('run the feasibility checks', 'An owner, admin or the program lead');
  } else if (plan && verification && verification.state === 'draft' && (awaitingFix > 0 || (issuedBundles.length > 0 && notHandedOff > 0))) {
    primary = <Button compact variant="primary" onClick={() => feas.current?.focusFirstIssued()}>Get the checks run</Button>;
  } else if (plan && verification && verification.state === 'draft' && issuedBundles.length > 0) {
    primary = <span className="gp-cb-chip gp-pv-chip" data-tone="info">{`Waiting for ${plural(issuedBundles.length, 'result')}`}</span>;
  } else if (plan && verification && verification.state === 'draft' && blockedChecks > 0 && plan.transition_origin === 'created') {
    primary = canDecide ? <Button compact variant="primary" onClick={onBack}>Choose another option</Button>
      : <><span className="gp-cb-chip gp-pv-chip">Waiting on GAC Pro review</span>{onBackToScope ? <Button compact variant="secondary" onClick={onBackToScope}>{`Back to ${label} Scope`}</Button> : null}</>;
  } else if (plan && lockedMove) {
    primary = <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="primary" to={`${prefix}/programs/${program}/scope/move`}>Open the move</Link>;
  } else if (plan && onBackToScope) {
    primary = <Button compact variant="primary" onClick={onBackToScope}>{`Back to ${label} Scope`}</Button>;
  }

  // Round-2 Medium: "Choose another option" appears once — the head primary, or the part head.
  const headChooses = !!plan && !!verification && verification.state === 'draft' && blockedChecks > 0 && plan.transition_origin === 'created' && canDecide
    && runnable === 0 && awaitingFix === 0 && issuedBundles.length === 0 && !confirmLock && !confirmRegen && !stale && signedCurrent;
  const td = docOf<TargetDesignContent>(docs, 'target_design');
  const build = docOf<BuildPlanContent>(docs, 'build_plan');
  const deploy = docOf<DeploymentPlanContent>(docs, 'deployment_plan');
  const ctrl = docOf<ControlConfigurationContent>(docs, 'control_configuration');
  const cut = docOf<CutoverPlanContent>(docs, 'cutover_plan');

  /** Exhaustive over the action kinds; an unknown kind shows no button (never a mislabelled link). */
  const actionEl = (a: PlanGapAction, who: string): ReactNode => {
    const aria = `${a.label} · ${who}`;
    const btn = (onClick: (() => void) | undefined) => (onClick ? <Button compact variant="ghost" aria-label={aria} onClick={onClick}>{a.label}</Button> : null);
    const link = (to: string) => <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" aria-label={aria} to={to}>{a.label}</Link>;
    switch (a.kind) {
      case 'choose_strategy': return canDecide ? btn(onBack) : <AskAdmin slug={slug} what="choose another strategy" subject={`${label}: ${who}`} compact />;
      case 'open_inventory': return btn(onOpenInventory);
      case 'open_transition': return link(`${prefix}/programs/${program}/scope/move`);
      case 'open_prepare': return link(`${prefix}/programs/${program}/prepare`);
      case 'generate': return canWrite ? btn(() => void generate()) : <AskAdmin slug={slug} what="generate the target plan" subject={`${label}: generate the target plan`} compact />;
      case 'open_today': return link(`${prefix}/today`);
      case 'record_evidence': return link(`${prefix}/programs/${program}/prepare`);
      default: return null;
    }
  };
  const gapRow = (g: PlanGap, key: string, who: string) => {
    // Tone follows what the viewer can do: amber only with an action element; otherwise neutral with the tag.
    const el = g.code === 'cancel_move' && g.action
      ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" aria-label={`${g.action.label} · ${who}`} to={`${prefix}/programs/${program}/scope/move?step=changes#cancel`}>{g.action.label}</Link>
      : g.action ? actionEl(g.action, who) : null;
    return (
      <li key={key} className="gp-pv-gap" data-tone={el ? 'act' : 'wait'} tabIndex={-1} data-gap-key={key}>
        <span><b>{`${who}: `}</b>{g.message}</span>
        <span className="gp-actions">{g.waiting_on_gacpro ? <span className="gp-cb-tag">Waiting on GAC Pro review</span> : null}{el ?? (g.waiting_on_gacpro ? null : <AskAdmin slug={slug} what={g.message.replace(/\.$/, '').toLowerCase()} subject={`${label}: ${who}`} compact />)}</span>
      </li>
    );
  };

  const moved = td?.services.filter((s) => s.change === 'replaced') ?? [];
  const gapsOf: Record<Kind, ReactNode[]> = {
    target_design: [...(td?.gaps ?? []).map((g, i) => gapRow(g, `td-${i}`, PART_LABEL.target_design)), ...(td?.services ?? []).flatMap((s) => s.gaps.map((g, i) => gapRow(g, `tds-${s.item_id}-${i}`, s.name)))],
    build_plan: [...(build?.iac ?? []).filter((x) => x.gap).map((x) => gapRow(x.gap!, `iac-${x.item_id}`, x.name ?? x.ref)), ...(build?.code_changes.gaps ?? []).map((g) => gapRow(g, `cc-${g.item_id}-${g.code}`, g.name ?? g.ref))],
    deployment_plan: [...(deploy?.gaps ?? []).map((g, i) => gapRow(g, `dg-${i}`, PART_LABEL.deployment_plan)), ...(deploy?.steps ?? []).filter((s) => s.gap).map((s) => gapRow(s.gap!, `ds-${s.step_key}`, s.title))],
    control_configuration: (ctrl?.components ?? []).filter((c) => c.gap).map((c) => gapRow(c.gap!, `cg-${c.component_id}`, c.name ?? c.component_key)),
    cutover_plan: (cut?.gaps ?? []).map((g, i) => gapRow(g, `cu-${i}`, PART_LABEL.cutover_plan)),
  };
  const openItems = Object.values(gapsOf).reduce((n, g) => n + g.length, 0);
  const templates = (build?.iac ?? []).flatMap((x) => x.templates.filter((t) => t.kind === 'iac_template').map((t) => ({ ...t, service: x.name ?? x.ref })));

  const part = (kind: Kind, summary: string, body: ReactNode) => {
    const has = docs?.some((d) => d.kind === kind);
    const gaps = gapsOf[kind];
    const shown = more[kind] ? gaps : gaps.slice(0, SHOWN);
    return (
      <div className="gp-pv-part" role="group" aria-labelledby={`pv-${kind}`}>
        <div className="gp-pv-part-head">
          <h4 id={`pv-${kind}`} className="gp-pv-part-h">{PART_LABEL[kind]}</h4>
          <span className="gp-dim">{has ? summary : 'Not generated yet'}</span>
        </div>
        {has && gaps.length ? (
          <ul className="gp-pv-gaps">
            {shown}
            {gaps.length > SHOWN ? <li><button type="button" className="gp-cb-textbtn" aria-expanded={!!more[kind]} onClick={() => {
              const opening = !more[kind];
              setMore((m) => ({ ...m, [kind]: opening }));
              if (opening) window.setTimeout(() => (document.querySelectorAll<HTMLElement>(`[aria-labelledby="pv-${kind}"] .gp-pv-gap`)[SHOWN])?.focus(), 0);
            }}>{more[kind] ? 'Show fewer' : `+${gaps.length - SHOWN} more`}<span className="sr-only">{` · ${PART_LABEL[kind]}`}</span></button></li> : null}
          </ul>
        ) : null}
        {has ? <details className="gp-cb-why gp-pv-more"><summary>Show all<span className="sr-only">{` · ${PART_LABEL[kind]}`}</span></summary>{body}</details> : null}
      </div>
    );
  };

  // H1: one status line (state chip · parts · open items), warn while anything is open.
  const parts = plan ? 5 - plan.missing.length : 0;
  const statusText = !plan ? '' : stale ? 'Verdicts changed after this strategy was chosen'
    : signedCurrent ? `Signed version ${latestPkg!.version}`
    : plan.missing.length ? `${parts} of 5 parts`
    : `${latestPkg ? 'Changed since it was signed · ' : ''}Ready to sign`;
  // H3 / V105-7-2: the signed plan names what the move waits for: its checks, the lock, or nothing.
  // H1: after signing, the bold line is the checks state; "Signed version N · date" is the sub-line.
  const checksHead = !verification ? null
    : verification.state === 'locked' ? `Locked ${fmtDate(verification.locked!.at)} · the move can be scheduled`
    : verification.state === 'spikes_passed' ? (verification.none_required ? 'No checks needed · ready to lock' : 'All checks passed · ready to lock')
    : `${passedChecks} of ${plural(totalChecks, 'check')} passed`;
  // H4: the part head counts once instead of repeating the blocked text on every row.
  const checksText = !verification ? 'Loading'
    : verification.state === 'locked' ? `Locked ${fmtDate(verification.locked!.at)}`
    : verification.none_required ? 'No feasibility check needed'
    : [unresolvedMoves ? `${plural(unresolvedMoves, 'move')} not resolved yet` : '', blockedChecks - unresolvedMoves ? `${plural(blockedChecks - unresolvedMoves, 'check')} not defined yet` : '',
      totalChecks - unresolvedMoves ? `${passedChecks} of ${totalChecks - unresolvedMoves} passed` : ''].filter(Boolean).join(' · ');
  const showChecksHead = !!plan && signedCurrent && !stale && plan.transition_origin === 'created' && !!checksHead;
  const statusSub = !plan ? '' : stale ? 'Confirm the strategy before generating again.'
    : signedCurrent ? (plan.transition_origin === 'joined' ? `${fmtDate(latestPkg!.signed_at)} · Your move isn't held by this plan.`
      : plan.transition_origin === 'created' ? `Signed version ${latestPkg!.version} · ${fmtDate(latestPkg!.signed_at)}`
      : `${fmtDate(latestPkg!.signed_at)} · Nothing moves under this plan.`)
    : openMove ? `Cutover ${fmtDate(openMove.planned_cutover_start)} · change it in the move` : `Opened ${fmtDate(plan.created_at)}`;
  const state = verification?.state ?? plan?.state ?? 'draft';
  const gapWaits = !!gap && !!verification && verification.state === 'draft' && (runnable > 0 || awaitingFix > 0 || issuedBundles.length > 0);
  const chip = !plan ? '' : state === 'locked' && showChecksHead ? 'Checks passed' : state === 'draft' && signedCurrent ? (plan.transition_origin === 'created' ? (gapWaits ? 'Waiting on GAC Pro' : verification?.blocked && !runnable && !issuedBundles.length && !awaitingFix ? 'Waiting on GAC Pro' : resultDue && !awaitingFix ? 'Waiting for results' : 'Checks pending') : 'Signed') : STATE_LABEL[state];
  const chipTone = !plan ? undefined : state === 'locked' || state === 'spikes_passed' ? 'ok'
    : signedCurrent && plan.transition_origin === 'created' ? (gapWaits || (resultDue && !awaitingFix) || (verification?.blocked && !runnable && !awaitingFix) ? 'info' : 'warn') : undefined;

  // H9: a result that arrives on a poll is announced once (no re-announce on unchanged refetches).
  const prevStatus = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    if (!verification) return;
    const now = new Map(verification.spikes.filter((s) => s.spike_key).map((s) => [s.spike_key!, s.status] as [string, string]));
    const prev = prevStatus.current;
    prevStatus.current = now;
    if (!prev) return;
    // Round-2 Nit: every check that changed in one poll is announced together.
    const changed = verification.spikes.filter((s) => s.spike_key && prev.has(s.spike_key) && prev.get(s.spike_key) !== s.status && ['passed', 'failed', 'awaiting_verified_result'].includes(s.status));
    if (!changed.length) return;
    const word = (st: string) => (st === 'passed' ? 'passed' : st === 'failed' ? 'failed' : 'not confirmed');
    if (changed.length === 1) {
      const c = changed[0]!; const t = spikeTitle(c);
      if (c.status === 'passed') show(`Feasibility check passed · ${t}`, verification.state === 'spikes_passed' ? 'You can lock the plan.' : undefined);
      else if (c.status === 'failed') show(`Feasibility check failed · ${t}`, 'Open "What failed" on the check to see why.');
      else show(`Result received · ${t}`, c.unconfirmed?.reason === 'identity_not_trusted' ? "This workflow isn't trusted yet. The check shows how to trust it." : "We couldn't confirm who ran it. The check shows how to fix it.");
    } else {
      show(`${changed.length} feasibility checks changed`, `${changed.map((c) => `${spikeTitle(c)}: ${word(c.status)}`).join('; ')}.${verification.state === 'spikes_passed' ? ' You can lock the plan.' : ''}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verification]);

  return (
    <Card>
      <div className="gp-inv gp-pv">
        <button type="button" className="gp-cb-back" onClick={onBack}><span aria-hidden="true">‹ </span>Back to strategies</button>
        <div className="gp-cb-head">
          <h3 className="gp-card-h" id="pv-h" ref={heading} tabIndex={-1}>Target plan</h3>
          <span className="gp-actions">
            {plan && !plan.missing.length && canWrite && !stale && (!complete || !!verification) ? (isLocked
              ? (canSign ? <Button compact variant="ghost" aria-expanded={confirmRegen} aria-busy={busy === 'generate' || undefined}
                  onClick={() => { setConfirmRegen(true); window.setTimeout(() => confirmRegenBtn.current?.focus(), 0); }}>{busy === 'generate' ? 'Generating…' : 'Regenerate'}</Button>
                : <AskAdmin slug={slug} what="regenerate the locked target plan" subject={`${label}: regenerate the locked target plan`} compact />)
              : <Button compact variant="ghost" aria-busy={busy === 'generate' || undefined} onClick={() => void generate()}>{busy === 'generate' ? 'Generating…' : 'Regenerate'}</Button>) : null}
            {primary}
          </span>
        </div>
        {!toastApi ? <p className="sr-only" role="status">{live}</p> : null}
        {error ? <Alert variant="error" onDismiss={() => setError(null)}><p>{error.text}</p>{error.action ? <span className="gp-actions">{error.action}</span> : null}</Alert> : null}
        {confirmLock && plan && verification?.state === 'spikes_passed' && checks.data?.can_lock ? (
          <div className="gp-cb-callout" data-tone="info" role="group" aria-labelledby="pv-lock-q" data-esc-owner=""
            onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setConfirmLock(false); heading.current?.focus(); } }}>
            <span id="pv-lock-q">{`Lock version ${latestPkg?.version ?? ''} with ${plural(passedChecks, 'passed check')}? Changing the plan later needs a new lock.`}</span>
            <span className="gp-actions">
              <Button ref={confirmLockBtn} compact variant="primary" aria-busy={busy === 'lock' || undefined} onClick={() => void lock()}>{busy === 'lock' ? 'Locking…' : 'Lock'}</Button>
              <Button compact variant="ghost" onClick={() => { setConfirmLock(false); heading.current?.focus(); }}>Cancel</Button>
            </span>
          </div>
        ) : null}
        {confirmRegen && isLocked && canSign ? (
          <div className="gp-cb-callout" data-tone="warn" role="group" aria-labelledby="pv-regen-q" data-esc-owner=""
            onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setConfirmRegen(false); heading.current?.focus(); } }}>
            <span id="pv-regen-q">This makes a new plan version. It has to be signed and locked again, and the move waits until then.</span>
            <span className="gp-actions">
              <Button ref={confirmRegenBtn} compact variant="primary" aria-busy={busy === 'generate' || undefined} onClick={() => void generate()}>{busy === 'generate' ? 'Generating…' : 'Regenerate'}</Button>
              <Button compact variant="ghost" onClick={() => { setConfirmRegen(false); heading.current?.focus(); }}>Cancel</Button>
            </span>
          </div>
        ) : null}

        {loading ? <Spinner label="Loading the target plan" /> : plans.error || detail.error ? (
          <Alert variant="error"><p>The target plan could not be loaded.</p><Button compact onClick={() => void refresh()}>Try again</Button></Alert>
        ) : !plan ? (
          <EmptyState title={decision ? 'The target plan is not open yet' : 'Choose a strategy first'}
            description={decision ? 'The chosen strategy is signed. Open its target plan to design the move.' : 'The target plan starts from the strategy you choose and sign.'} />
        ) : (
          <>
            <div className="gp-cb-callout" data-tone={stale || openItems > 0 || !signedCurrent ? 'warn' : 'info'}>
              <span>
                <span className="gp-cb-chip gp-pv-chip" data-tone={chipTone}>{chip}</span>{' '}
                <b>{`${showChecksHead ? checksHead : statusText}${!stale && openItems ? ` · ${plural(openItems, 'open item')}` : ''}`}</b>{' '}
                <span className="gp-dim">{statusSub}</span>
              </span>
              {latestPkg ? (
                <span className="gp-actions">
                  {joinedMove ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" to={`${prefix}/programs/${program}/scope/move`}>Open the move</Link> : null}
                  <Button compact variant="ghost" onClick={() => void copyVerify()}>{signedCurrent ? 'Copy verify link' : `Copy verify link (version ${latestPkg.version})`}</Button>
                  <Button compact variant="ghost" aria-busy={busy === 'download' || undefined} onClick={() => void download()}>{busy === 'download' ? 'Preparing…' : 'Download signed plan'}</Button>
                </span>
              ) : null}
            </div>
            {verification?.previously_locked && !isLocked ? (
              <p className="gp-dim">{`Version locked ${fmtDate(verification.previously_locked.at)} was replaced · checks that didn't change carry over`}</p>
            ) : null}
            {previous?.latest_package ? (
              <p className="gp-dim">{`Replaces the target plan signed ${fmtDate(previous.latest_package.signed_at)} (version ${previous.latest_package.version}) · `}
                <a href={`/verify/infra-plan/${encodeURIComponent(previous.latest_package.package_id)}`}>verify it</a></p>
            ) : null}
            {(plan.missing.length || forceDate) && canWrite && !stale ? (
              <form id="pv-generate" className="gp-pv-form" onSubmit={(e) => void generate(e)} noValidate>
                {needsDate ? (
                  <Field id="pv-cutover" label="Planned cutover date" required error={cutoverError} hint="When the boundary moves to the target. You can change it in the move later.">
                    <Input ref={dateField} type="date" value={cutover} onChange={(e) => { setCutover(e.currentTarget.value); setCutoverError(''); }} />
                  </Field>
                ) : null}
              </form>
            ) : null}

            {complete ? (
              <div className="gp-pv-part" role="group" aria-labelledby="pv-checks">
                <div className="gp-pv-part-head">
                  <h4 id="pv-checks" className="gp-pv-part-h">Feasibility checks</h4>
                  <span className="gp-dim">{checksText}</span>
                  {blockedChecks && !isLocked && !headChooses ? (canDecide ? <Button compact variant="ghost" onClick={onBack}>Choose another option</Button>
                    : <AskAdmin slug={slug} what="choose another option for a check GAC Pro hasn't written yet" subject={`${label}: choose another option`} compact />) : null}
                  {resultDue && checks.dataUpdatedAt ? (
                    <span className="gp-dim">{`Last checked ${fmtTime(checks.dataUpdatedAt)} · `}
                      <button type="button" className="gp-cb-textbtn" aria-busy={checks.isFetching || undefined} onClick={() => void checks.refetch()}>{checks.isFetching ? 'Checking…' : 'Check now'}</button></span>
                  ) : null}
                </div>
                {checks.isError ? <Alert variant="error"><p>The feasibility checks could not be loaded.</p><Button compact onClick={() => void checks.refetch()}>Try again</Button></Alert>
                  : verification ? <PlanFeasibility ref={feas} slug={slug} planId={plan.plan_id} label={label} spikes={verification.spikes} canIssue={!!checks.data?.can_issue}
                      canManage={!!checks.data?.can_lock} canDecide={canDecide} onChooseOther={onBack} onChanged={refresh} onFormOpenChange={setFormOpen}
                      handedOff={handedOff} onHandedOff={markHandedOff} renderAction={actionEl}
                      onIssued={(title) => show(`Feasibility check issued · ${title}`, 'Hand it to whoever runs CI: the steps are under the check.')}
                      onError={(text) => setError({ text })} /> : <Spinner label="Loading the feasibility checks" />}
              </div>
            ) : null}
            {part('target_design', `${plural(moved.length, 'service')} ${moved.length === 1 ? 'moves' : 'move'}${td?.inheritance ? ` · ${plural(td.inheritance.gained, 'control')} become provider-held (to confirm)` : ''}`, (
              <ul className="gp-pv-list">
                {(td?.services ?? []).map((s) => (
                  <li key={s.item_id}><b>{s.name}</b> <span className="gp-dim">{`${CHANGE_LABEL[s.change]}${s.change === 'replaced' ? ` · ${s.label}` : ''} · ${s.ref}`}</span></li>
                ))}
              </ul>
            ))}
            {part('build_plan', `${plural(templates.length, 'infrastructure template')} · ${plural(build?.code_changes.tasks.length ?? 0, 'code change')}`, (
              <>
                {templates.length ? (
                  <ul className="gp-pv-list">
                    {templates.map((t) => (
                      <li key={`${t.template_id}:${t.service}`}><b>{t.title ?? t.template_id}</b> <span className="gp-dim">{[t.service, t.tool, `version ${t.version}`].filter(Boolean).join(' · ')}</span>
                        {t.rendered.map((r) => (
                          <div key={r.component_key} className="gp-pv-files">
                            {r.files.map((f) => (
                              <details key={f.path} className="gp-cb-why">
                                <summary>{`${r.component_key}/${f.path}`}</summary>
                                <pre className="gp-pv-pre" tabIndex={0} role="region" aria-label={`${r.component_key}/${f.path} preview`}>{f.content}</pre>
                                <Button compact variant="ghost" aria-label={`Download ${r.component_key}/${f.path}`} onClick={() => saveFile(f.path, f.content, 'text/plain')}>Download</Button>
                              </details>
                            ))}
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <ul className="gp-pv-list">
                  {(build?.code_changes.tasks ?? []).map((t) => (
                    <li key={t.task_key}><b>{`${t.title} · ${t.name ?? t.ref}`}</b>{t.provenance === 'customer_reported' ? <span className="gp-dim"> · from your CI's scan</span> : null}
                      <ul className="gp-pv-files">{t.files.map((f) => <li key={`${f.path}:${f.line}`}>{f.url ? <a href={f.url} target="_blank" rel="noreferrer">{`${f.path}:${f.line}`}</a> : `${f.path}:${f.line}`}</li>)}</ul>
                    </li>
                  ))}
                </ul>
              </>
            ))}
            {part('deployment_plan', `${plural(deploy?.steps.length ?? 0, 'step')} · ${plural(deploy?.pipeline_changes.length ?? 0, 'pipeline change')}`, (
              <>
                <ol className="gp-pv-list">{(deploy?.steps ?? []).map((s) => <li key={s.step_key}>{s.title} <span className="gp-cb-tag">{ENV_LABEL[s.environment]}</span></li>)}</ol>
                {deploy?.pipeline_changes.length ? (
                  <ul className="gp-pv-files">{deploy.pipeline_changes.map((p) => <li key={`${p.path}:${p.line}`}>{p.url ? <a href={p.url} target="_blank" rel="noreferrer">{`${p.path}:${p.line}`}</a> : `${p.path}:${p.line}`} <span className="gp-dim">{p.change}{p.provenance === 'customer_reported' ? " · from your CI's scan" : ''}</span></li>)}</ul>
                ) : null}
              </>
            ))}
            {part('control_configuration', `${ctrl?.summary.with_baseline ?? 0} of ${plural(ctrl?.summary.components ?? 0, 'component')} have a secure baseline`, (
              <ul className="gp-pv-list">
                {(ctrl?.components ?? []).map((c) => (
                  <li key={c.component_id}><b>{c.name ?? c.component_key}</b>{' '}
                    {c.status === 'baseline' ? (
                      <ul className="gp-pv-ksis">
                        {c.ksis.map((k) => (
                          <li key={k.ksi_id}>
                            {k.title ? <><span className="gp-pv-ksi">{k.title}</span> </> : null}
                            <span className="gp-cb-tag">{k.ksi_id}</span>
                            {` · ${plural(k.sources.length, 'vendor source')}, ${plural(k.checks.length, 'check')}`}
                          </li>
                        ))}
                      </ul>
                    ) : <span className="gp-dim">No baseline yet</span>}</li>
                ))}
              </ul>
            ))}
            {part('cutover_plan', cut?.transition ? `Cutover ${fmtDate(cut.transition.planned_cutover_start)} · ${plural(cut.milestones.items.length, 'milestone')}${cut.milestones.status === 'projected' ? (plan.transition_origin === 'joined' ? ' (on Today once the move is scheduled)' : ' (on Today once the plan locks)') : ' on Today'}` : 'No cutover', (
              <>
                <ul className="gp-pv-list">
                  {(cut?.data_migration ?? []).map((d) => <li key={`${d.item_id}:${d.to_component_key}`}>{`Copy the data from ${d.from_name ?? d.from_component_key} to ${d.to_name ?? d.to_component_key}`}{d.method ? <span className="gp-dim">{` · ${d.method}`}</span> : null}</li>)}
                  {(cut?.rollback ?? []).map((r) => <li key={`rb:${r.component_key}`}>{`Rollback: ${r.how}`}</li>)}
                  {(cut?.reporting ?? []).map((r) => (
                    <li key={`rp:${r.profile_id}`}>{`${programLabel(r.program)} ${r.kind === 'fedramp_scn' ? 'change notification' : 'change notice'}: `}
                      <span className="gp-dim">{r.status === 'filed_when_planned' ? 'filed when the move is scheduled' : r.kind === 'fedramp_scn' ? (r.schema_valid ? 'ready to send' : `needs ${plural(r.incomplete.length, 'answer')}`) : (r.notice?.status ?? 'draft')}</span></li>
                  ))}
                </ul>
                <ol className="gp-pv-list">{(cut?.milestones.items ?? []).map((m) => <li key={`${m.key}:${m.profile_id ?? ''}`}>{m.title} <span className="gp-dim">{m.scheduled_for ? fmtDate(m.scheduled_for) : WINDOW_LABEL[m.window_status] ?? ''}</span></li>)}</ol>
              </>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}
